// src/pages/AdminReferralLinks.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    Table,
    Button,
    Spinner,
    Alert,
    Badge,
    Form,
    InputGroup,
    Modal
} from 'react-bootstrap';
import { Search, Eye, ToggleOff, ToggleOn } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const AdminReferralLinks = () => {
    const [referralLinks, setReferralLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [processing, setProcessing] = useState(null);
    const [selectedLink, setSelectedLink] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [linkDetails, setLinkDetails] = useState(null);

    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        fetchReferralLinks();
    }, []);

    const fetchReferralLinks = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/admin/referral-links');
            setReferralLinks(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.loadingError'));
            console.error('Error fetching referral links:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (linkId, currentState) => {
        setProcessing(linkId);

        try {
            // Обновляем только статус активности
            await api.put(`/api/admin/referral-links/${linkId}`, { active: !currentState });

            // Обновляем состояние в списке
            setReferralLinks(referralLinks.map(link =>
                link._id === linkId ? { ...link, active: !currentState } : link
            ));
        } catch (err) {
            setError(err.response?.data?.message || t('errors.updateError'));
            console.error('Error updating referral link:', err);
        } finally {
            setProcessing(null);
        }
    };

    const handleViewDetails = async (link) => {
        setSelectedLink(link);
        setShowDetailsModal(true);
        setDetailsLoading(true);

        try {
            const response = await api.get(`/api/admin/referral-links/${link._id}`);
            if (response.data.success) {
                setLinkDetails(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching link details:', err);
        } finally {
            setDetailsLoading(false);
        }
    };

    // Фильтрация по поисковому запросу
    const filteredLinks = referralLinks.filter(link =>
        (link.name && link.name.toLowerCase().includes(search.toLowerCase())) ||
        (link.code && link.code.toLowerCase().includes(search.toLowerCase())) ||
        (link.sellerName && link.sellerName.toLowerCase().includes(search.toLowerCase()))
    );

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    return (
        <Container className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>{t('admin.referralLinks')}</h1>
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            <Card className="glass-card">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{t('admin.referralLinksList')}</h5>
                    <Form.Group style={{ width: '300px' }}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder={t('admin.searchLinks')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </InputGroup>
                    </Form.Group>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">{t('common.loading')}</p>
                        </div>
                    ) : filteredLinks.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="web3-table">
                                <thead>
                                <tr>
                                    <th>{t('referral.code')}</th>
                                    <th>{t('common.name')}</th>
                                    <th>{t('common.seller')}</th>
                                    <th>{t('flightSheet.poolSettings')}</th>
                                    <th>{t('referral.referrals')}</th>
                                    <th>{t('common.status')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredLinks.map(link => (
                                    <tr key={link._id}>
                                        <td><code>{link.code}</code></td>
                                        <td>{link.name}</td>
                                        <td>
                                            {link.sellerName || '-'}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span>{link.poolHost}:{link.poolPort}</span>
                                                <small className="text-muted">
                                                    {link.poolWorker}
                                                </small>
                                            </div>
                                        </td>
                                        <td>{link.usedCount || 0}</td>
                                        <td>
                                            {link.active ? (
                                                <Badge bg="success" pill>{t('common.active')}</Badge>
                                            ) : (
                                                <Badge bg="secondary" pill>{t('common.inactive')}</Badge>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleViewDetails(link)}
                                                >
                                                    <Eye />
                                                </Button>
                                                <Button
                                                    variant={link.active ? "outline-warning" : "outline-success"}
                                                    size="sm"
                                                    onClick={() => handleToggleActive(link._id, link.active)}
                                                    disabled={processing === link._id}
                                                >
                                                    {processing === link._id ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : link.active ? (
                                                        <ToggleOff />
                                                    ) : (
                                                        <ToggleOn />
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            {search ? (
                                <p>{t('referral.noSearchResults')}</p>
                            ) : (
                                <p>{t('admin.noReferralLinks')}</p>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Модальное окно с деталями ссылки */}
            <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedLink && (
                            <span>
                                {t('referral.linkDetails')}: {selectedLink.name}
                                <code className="ms-2">{selectedLink.code}</code>
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailsLoading ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">{t('common.loading')}</p>
                        </div>
                    ) : linkDetails ? (
                        <div>
                            <h5>{t('referral.linkInfo')}</h5>
                            <Table bordered className="mb-4">
                                <tbody>
                                <tr>
                                    <td><strong>{t('common.seller')}</strong></td>
                                    <td>{linkDetails.sellerName}</td>
                                </tr>
                                <tr>
                                    <td><strong>{t('common.name')}</strong></td>
                                    <td>{linkDetails.name}</td>
                                </tr>
                                <tr>
                                    <td><strong>{t('referral.code')}</strong></td>
                                    <td><code>{linkDetails.code}</code></td>
                                </tr>
                                <tr>
                                    <td><strong>{t('common.status')}</strong></td>
                                    <td>
                                        {linkDetails.active ? (
                                            <Badge bg="success">{t('common.active')}</Badge>
                                        ) : (
                                            <Badge bg="secondary">{t('common.inactive')}</Badge>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>{t('flightSheet.poolSettings')}</strong></td>
                                    <td>
                                        {linkDetails.poolHost}:{linkDetails.poolPort}<br />
                                        <small className="text-muted">{linkDetails.poolWorker}</small>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>{t('flightSheet.createdAt')}</strong></td>
                                    <td>{formatDate(linkDetails.createdAt)}</td>
                                </tr>
                                <tr>
                                    <td><strong>{t('referral.usedCount')}</strong></td>
                                    <td>{linkDetails.usedCount || 0}</td>
                                </tr>
                                </tbody>
                            </Table>

                            {linkDetails.referredUsers && linkDetails.referredUsers.length > 0 ? (
                                <>
                                    <h5>{t('referral.referredUsers')}</h5>
                                    <Table hover responsive>
                                        <thead>
                                        <tr>
                                            <th>{t('common.user')}</th>
                                            <th>{t('referral.registrationDate')}</th>
                                            <th>{t('referral.lastLogin')}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {linkDetails.referredUsers.map(user => (
                                            <tr key={user._id}>
                                                <td>{user.username}</td>
                                                <td>{formatDate(user.createdAt)}</td>
                                                <td>{formatDate(user.lastLogin)}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </>
                            ) : (
                                <div className="text-center py-3">
                                    <p className="text-muted">{t('referral.noReferredUsers')}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-3">
                            <p className="text-muted">{t('errors.loadingError')}</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDetailsModal(false)}>
                        {t('common.close')}
                    </Button>
                    {selectedLink && linkDetails && (
                        <Button
                            variant={linkDetails.active ? "outline-warning" : "outline-success"}
                            onClick={() => {
                                handleToggleActive(selectedLink._id, linkDetails.active);
                                setLinkDetails({...linkDetails, active: !linkDetails.active});
                            }}
                        >
                            {linkDetails.active ? t('common.deactivate') : t('common.activate')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminReferralLinks;